import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../lib/api';

/**
 * Voice input via record-then-transcribe, NOT the browser Web Speech API.
 *
 * Web Speech was a dead end on the phone-first target: Android Chrome
 * force-ends a recognition session at every natural pause, and the next session
 * re-recognizes the trailing audio — which duplicated dictated text over and
 * over, unfixably from page JS (three attempts proved it). Brave withholds the
 * events entirely. Here the phone records ONE clip with MediaRecorder and posts
 * it once to /api/transcribe/ (OpenRouter server-side): no sessions, no
 * restarts, no duplication, and it works in every browser that can record.
 *
 * Each finished clip's text arrives via the onTranscript callback — one clean
 * chunk, so callers just append it. No interim transcript and no live streaming;
 * `transcribing` is true while the clip is uploading.
 */

// Prefer a container the transcription API takes as-is. Safari/iOS only offers
// mp4; leaving the type empty lets that browser pick its own default.
function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

// Mirror the server's bounds so we fail fast without spending an upload.
const MIN_BLOB_BYTES = 1024;
const MAX_BLOB_BYTES = 10 * 1024 * 1024;

export default function useVoiceInput({ onTranscript } = {}) {
  const [listening, setListening] = useState(false); // i.e. recording
  const [transcribing, setTranscribing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const cancelledRef = useRef(false);
  const transcriptRef = useRef('');
  const onTranscriptRef = useRef(onTranscript);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  // Recording needs MediaRecorder + getUserMedia + a secure context. On a
  // plain-http LAN dev box getUserMedia is absent, so the mic hides itself.
  const supported =
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof MediaRecorder !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  const releaseMic = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const upload = useCallback(async (blob, type) => {
    setTranscribing(true);
    try {
      const ext = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm';
      const form = new FormData();
      form.append('audio', blob, `clip.${ext}`);
      // Clear the JSON default so the browser sets multipart/form-data with its
      // boundary; otherwise the server can't find the field and returns 400.
      const res = await api.post('/transcribe/', form, {
        timeout: 120000,
        headers: { 'Content-Type': undefined },
      });
      const text = (res.data?.text || '').trim();
      if (!text) {
        setError('no-speech');
        return;
      }
      setError(null);
      transcriptRef.current = transcriptRef.current
        ? `${transcriptRef.current} ${text}`
        : text;
      setTranscript(transcriptRef.current);
      onTranscriptRef.current?.(text);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) setError('rate-limited');
      else if (status === 413) setError('too-long');
      else setError(err.response?.data?.error ? 'transcribe-failed' : 'transcribe-failed');
    } finally {
      setTranscribing(false);
    }
  }, []);

  const start = useCallback(async () => {
    if (!supported || listening || transcribing) return;

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setError(
        e.name === 'NotAllowedError' || e.name === 'SecurityError'
          ? 'not-allowed'
          : 'no-mic'
      );
      return;
    }

    cancelledRef.current = false;
    streamRef.current = stream;
    chunksRef.current = [];
    setError(null);

    const mimeType = pickMimeType();
    let recorder;
    try {
      // No forced bitrate: a mobile encoder that dislikes the value can run
      // happily while producing no data at all.
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      releaseMic();
      setError('unsupported');
      return;
    }

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onerror = () => {
      releaseMic();
      setListening(false);
      setError('record-failed');
    };
    recorder.onstop = () => {
      releaseMic();
      setListening(false);
      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (cancelledRef.current) return;
      if (blob.size < MIN_BLOB_BYTES) {
        setError('empty');
        return;
      }
      if (blob.size > MAX_BLOB_BYTES) {
        setError('too-long');
        return;
      }
      upload(blob, type);
    };

    recorderRef.current = recorder;
    // Timeslice so a data callback fires every second rather than relying on the
    // final flush at stop(), which mobile encoders are unreliable about.
    recorder.start(1000);
    setListening(true);
  }, [supported, listening, transcribing, releaseMic, upload]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      // Flush whatever the encoder is holding so the final second isn't dropped.
      try {
        recorder.requestData();
      } catch {
        /* not all engines allow it */
      }
      try {
        recorder.stop();
      } catch {
        /* already stopped */
      }
    }
    recorderRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stop();
  }, [stop]);

  const reset = useCallback(() => {
    transcriptRef.current = '';
    setTranscript('');
    setError(null);
  }, []);

  useEffect(
    () => () => {
      cancelledRef.current = true;
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {
          /* nothing to stop */
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    },
    []
  );

  return {
    supported,
    listening,
    transcribing,
    transcript,
    error,
    start,
    stop,
    cancel,
    reset,
    engine: supported ? 'server' : 'none',
  };
}
